import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  UploadFileOutlined as UploadFileOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';

type BankAccount = {
  id: string;
  name?: string | null;
  bankName?: string | null;
  accountLast4?: string | null;
  projectIds?: Array<string | number> | null;
};

type StatementAccountDetection = {
  bank: BankAccount;
  last4: string;
};

type BankTransaction = {
  id: string;
  bankAccountId?: string | null;
  txnType?: 'receipt' | 'payment' | null;
  amount?: number | null;
  txnDate?: any;
  referenceNo?: string | null;
  notes?: string | null;
  isReconciled?: boolean | null;
  createdAt?: any;
};

type ReconciliationDoc = {
  id: string;
  bankAccountId: string;
  rangeKey?: string | null;
  rangeStartDate?: string | null;
  rangeEndDate?: string | null;
  monthKey: string;
  statementReceipts?: number | null;
  statementPayments?: number | null;
  notes?: string | null;
  finalized?: boolean | null;
  updatedAt?: any;
  updatedBy?: string | null;
  openingBalance?: number | null;
  statementClosingBalance?: number | null;
  statementToDate?: string | null;
  statementImportedCount?: number | null;
  statementMatchedCount?: number | null;
  statementFile?: {
    name?: string | null;
    url?: string | null;
    path?: string | null;
    size?: number | null;
    uploadedAt?: any;
    uploadedBy?: string | null;
  } | null;
  draftStatementRows?: Array<{ dateYmd: string; description: string; debit: number; credit: number; refNo: string }> | null;
};

const isReconciliationDone = (row: ReconciliationDoc | null | undefined) =>
  Boolean((row as any)?.finalized || (row as any)?.reconciledAt);

const isTxnReconciled = (row: any) => {
  const raw = row?.isReconciled;
  if (raw === true) return true;
  const normalized = String(raw ?? '').trim().toLowerCase();
  if (normalized === 'true' || normalized === 'yes' || normalized === '1') return true;
  return Boolean(String(row?.reconciliationRangeKey || '').trim());
};

const toMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
  if (typeof value?.seconds === 'number') return Number(value.seconds || 0) * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toYmd = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const todayYmd = () => toYmd(new Date());

const startOfDayMs = (ymd: string) => {
  if (!ymd) return 0;
  return Date.parse(`${ymd}T00:00:00`);
};

const endOfDayMs = (ymd: string) => {
  if (!ymd) return 0;
  return Date.parse(`${ymd}T23:59:59.999`);
};

const rangeKeyFrom = (start: string, end: string) => `${start}__${end}`;

const monthKeyFromYmd = (ymd: string) => String(ymd || '').slice(0, 7);

const isFullMonthRange = (start: string, end: string) => {
  if (!start || !end) return false;
  const s = new Date(`${start}T00:00:00`);
  if (Number.isNaN(s.getTime())) return false;
  const first = toYmd(new Date(s.getFullYear(), s.getMonth(), 1));
  const last = toYmd(new Date(s.getFullYear(), s.getMonth() + 1, 0));
  return start === first && end === last;
};

const toAmount = (value: any) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const FIRST_RECON_START_DATE = '2026-02-22';

const parseStatementDate = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : toYmd(parsed);
};

const ymdToDdMmYyyy = (ymd: string): string => {
  const raw = String(ymd || '').trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const parseSbiDate = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const m = raw.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!m) return parseStatementDate(raw);
  const day = Number(m[1]);
  const mon = String(m[2] || '').toLowerCase();
  const year = Number(m[3]);
  const monthMap: Record<string, number> = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
    jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  };
  const mm = monthMap[mon];
  if (!mm) return '';
  return `${year}-${String(mm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const parseContraFromStatement = (description: string, refNo: string) => {
  const text = `${String(refNo || '').trim()} ${String(description || '').trim()}`.replace(/\s+/g, ' ').trim();
  const match = text.match(/TRANSFER\s+(FROM|TO)\s+([0-9]{6,18})/i);
  if (!match) {
    return { isContra: false, direction: '' as '' | 'from' | 'to', accountNo: '', last4: '' };
  }
  const direction = String(match[1] || '').toLowerCase() === 'from' ? 'from' : 'to';
  const accountNo = String(match[2] || '').trim();
  const last4 = accountNo.slice(-4);
  return { isContra: true, direction: direction as 'from' | 'to', accountNo, last4 };
};

const toPaise = (value: number) => Math.round(toAmount(value) * 100);

const addDays = (ymd: string, days: number) => {
  const ms = startOfDayMs(ymd);
  if (!ms) return ymd;
  const d = new Date(ms);
  d.setDate(d.getDate() + days);
  return toYmd(d);
};

const accountLast4FromBank = (bank: BankAccount): string => {
  const explicit = String((bank as any)?.accountLast4 || '').trim();
  if (explicit) return explicit.slice(-4);
  const fromName = `${String(bank?.name || '')} ${String((bank as any)?.bankName || '')}`;
  const m = fromName.match(/([0-9]{4})\b/);
  return m ? String(m[1]) : '';
};

const parseAmountToken = (value: string) =>
  toAmount(String(value || '').replace(/["',₹\s]/g, ''));

const parseBalanceToken = (value: string): number | null => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parenNegative = /^\(.*\)$/.test(raw);
  const match = raw.match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!match?.[0]) return null;
  const amount = toAmount(String(match[0]).replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  if (parenNegative && amount > 0) return -amount;
  return amount;
};

const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      row.push(field);
      field = '';
      continue;
    }
    if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      if (row.some((c) => String(c || '').trim())) rows.push(row);
      row = [];
      continue;
    }
    field += ch;
  }
  row.push(field);
  if (row.some((c) => String(c || '').trim())) rows.push(row);
  return rows;
};

const parseStatementRowsFromTsv = (text: string) => {
  const lines = String(text || '').split(/\r?\n/);
  const headerIdx = lines.findIndex((l) => l.includes('Txn Date') && l.includes('\t') && l.toLowerCase().includes('balance'));
  if (headerIdx < 0) {
    return {
      rows: [] as Array<{ dateYmd: string; description: string; debit: number; credit: number; refNo: string }>,
      statementDate: '',
      closingBalance: null as number | null,
    };
  }
  const parsed: Array<{ dateYmd: string; description: string; debit: number; credit: number; refNo: string }> = [];
  let lastBalance: number | null = null;
  for (let i = headerIdx + 1; i < lines.length; i += 1) {
    const line = String(lines[i] || '').trim();
    if (!line) continue;
    if (line.startsWith('**')) break;
    const cols = lines[i].split('\t').map((x) => String(x || '').trim());
    if (cols.length < 8) continue;
    const dateYmd = parseSbiDate(cols[0]);
    const description = String(cols[2] || '').trim();
    const refNo = String(cols[3] || '').trim();
    const debit = parseAmountToken(cols[5]);
    const credit = parseAmountToken(cols[6]);
    const balance = parseBalanceToken(String(cols[7] || ''));
    if (!dateYmd || !(debit > 0 || credit > 0)) continue;
    if (balance !== null) lastBalance = balance;
    parsed.push({ dateYmd, description, debit, credit, refNo });
  }
  const statementDate = parsed
    .map((r) => r.dateYmd)
    .sort((a, b) => startOfDayMs(b) - startOfDayMs(a))[0] || '';
  return { rows: parsed, statementDate, closingBalance: lastBalance };
};

export default function OfficeBankReconciliationPage() {
  const { currentUser, currentProjectId } = useAuth();
  const db = useMemo(() => getFirestore(), []);

  const [rangeStart, setRangeStart] = useState(FIRST_RECON_START_DATE);
  const [rangeEnd, setRangeEnd] = useState(todayYmd());
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [reconciliations, setReconciliations] = useState<ReconciliationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [reconciliationNotes, setReconciliationNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingBalanceTouched, setOpeningBalanceTouched] = useState(false);
  const [statementClosingBalance, setStatementClosingBalance] = useState('');
  const [statementToDate, setStatementToDate] = useState(todayYmd());
  const [statementRows, setStatementRows] = useState<
    Array<{ dateYmd: string; description: string; debit: number; credit: number; refNo: string }>
  >([]);
  const [manualMatches, setManualMatches] = useState<Record<number, string>>({});
  const [creatingMatchRow, setCreatingMatchRow] = useState<number | null>(null);
  const [createDialogRow, setCreateDialogRow] = useState<number | null>(null);
  const [createDialogPartyName, setCreateDialogPartyName] = useState('');
  const [createDialogContraName, setCreateDialogContraName] = useState('');
  const [customPartyNames, setCustomPartyNames] = useState<string[]>([]);
  const [statementSwitchDialogOpen, setStatementSwitchDialogOpen] = useState(false);
  const [pendingStatementSwitch, setPendingStatementSwitch] = useState<{
    ext: string;
    text: string;
    fileName: string;
    detected: StatementAccountDetection;
  } | null>(null);
  const [continueImportAfterSwitch, setContinueImportAfterSwitch] = useState(false);
  const [statementDropActive, setStatementDropActive] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const lastSelectedAccountRef = useRef<string>('');

  const selectedRangeKey = useMemo(() => rangeKeyFrom(rangeStart, rangeEnd), [rangeEnd, rangeStart]);
  const selectedMonthKey = useMemo(() => monthKeyFromYmd(rangeStart), [rangeStart]);
  const selectedIsFullMonth = useMemo(() => isFullMonthRange(rangeStart, rangeEnd), [rangeEnd, rangeStart]);
  const bankHeaderCards = useMemo(() => {
    return bankAccounts
      .slice()
      .sort((left, right) =>
        String(right.name || right.bankName || right.id).localeCompare(
          String(left.name || left.bankName || left.id)
        )
      )
      .map((account) => {
      const accountId = String(account.id || '').trim();
      const accountTxns = transactions.filter((row) => String(row.bankAccountId || '').trim() === accountId);
      const currentBalance = accountTxns.reduce((sum, row) => {
        const amount = toAmount((row as any)?.amount);
        const isPayment = String((row as any)?.txnType || '').toLowerCase() === 'payment';
        return sum + (isPayment ? -amount : amount);
      }, 0);
      const latestReconciled =
        reconciliations
          .filter((row) => String(row.bankAccountId || '').trim() === accountId)
          .filter((row) => isReconciliationDone(row))
          .slice()
          .sort((a, b) => {
            const ae = startOfDayMs(String((a as any)?.rangeEndDate || ''));
            const be = startOfDayMs(String((b as any)?.rangeEndDate || ''));
            if (be !== ae) return be - ae;
            return toMs((b as any)?.updatedAt) - toMs((a as any)?.updatedAt);
          })[0] || null;
      return {
        id: accountId,
        name: String(account.name || account.bankName || accountId),
        currentBalance,
        reconciledBalance: toAmount((latestReconciled as any)?.statementClosingBalance),
        reconciledDate: String((latestReconciled as any)?.rangeEndDate || ''),
      };
    });
  }, [bankAccounts, transactions, reconciliations]);

  useEffect(() => {
    setBankAccounts([]);
    setTransactions([]);
    setReconciliations([]);
    setError(null);
    if (!currentProjectId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);

    const unsubBanks = onSnapshot(
      query(collection(db, 'bankAccounts'), orderBy('name', 'asc'), limit(500)),
      (snap) => {
        const pid = String(currentProjectId || '');
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }) as BankAccount)
          .filter((row: any) =>
            Array.isArray(row.projectIds) && row.projectIds.map((x: any) => String(x)).includes(pid)
          );
        setBankAccounts(rows);
      },
      (err) => {
        console.error('Load bank accounts failed', err);
        setError(err?.message || 'Failed to load bank accounts.');
        setBankAccounts([]);
      }
    );

    const unsubTx = onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'bankTransactions'), orderBy('createdAt', 'desc'), limit(5000)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }) as BankTransaction);
        setTransactions(rows);
      },
      (err) => {
        console.error('Load bank transactions failed', err);
        setError(err?.message || 'Failed to load bank transactions.');
        setTransactions([]);
      }
    );

    const unsubRecon = onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'bankReconciliations'), orderBy('updatedAt', 'desc'), limit(1000)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }) as ReconciliationDoc);
        setReconciliations(rows);
      },
      (err) => {
        console.error('Load reconciliations failed', err);
        setError(err?.message || 'Failed to load reconciliations.');
        setReconciliations([]);
      }
    );

    setLoading(false);
    return () => {
      unsubBanks();
      unsubTx();
      unsubRecon();
    };
  }, [currentProjectId, db]);

  const reconciliationByAccount = useMemo(() => {
    const map = new Map<string, ReconciliationDoc>();
    reconciliations
      .filter((row) => {
        const key = String(row.rangeKey || '').trim();
        if (key && key === selectedRangeKey) return true;
        const legacyMonth = String(row.monthKey || '').trim();
        return !key && selectedIsFullMonth && legacyMonth === selectedMonthKey;
      })
      .forEach((row) => map.set(String(row.bankAccountId || '').trim(), row));
    return map;
  }, [reconciliations, selectedIsFullMonth, selectedMonthKey, selectedRangeKey]);

  const rangeTransactions = useMemo(
    () =>
      transactions.filter((row) => {
        const ms = toMs(row.txnDate) || toMs(row.createdAt);
        if (!ms) return false;
        const fromMs = startOfDayMs(rangeStart);
        const toMsValue = endOfDayMs(rangeEnd);
        return ms >= fromMs && ms <= toMsValue;
      }),
    [rangeEnd, rangeStart, transactions]
  );

  const monthlySystemByAccount = useMemo(() => {
    const map = new Map<
      string,
      { receiptTotal: number; paymentTotal: number; net: number; count: number }
    >();
    rangeTransactions.forEach((row) => {
      const accountId = String(row.bankAccountId || '').trim();
      if (!accountId) return;
      const prev = map.get(accountId) || { receiptTotal: 0, paymentTotal: 0, net: 0, count: 0 };
      const amt = toAmount(row.amount);
      if (String(row.txnType || '').toLowerCase() === 'payment') {
        prev.paymentTotal += amt;
        prev.net -= amt;
      } else {
        prev.receiptTotal += amt;
        prev.net += amt;
      }
      prev.count += 1;
      map.set(accountId, prev);
    });
    return map;
  }, [rangeTransactions]);

  const rows = useMemo(() => {
    return bankAccounts.map((account) => {
      const accountId = String(account.id || '').trim();
      const system = monthlySystemByAccount.get(accountId) || {
        receiptTotal: 0,
        paymentTotal: 0,
        net: 0,
        count: 0,
      };
      const rec = reconciliationByAccount.get(accountId) || null;
      const statementReceiptsValue = toAmount(rec?.statementReceipts);
      const statementPaymentsValue = toAmount(rec?.statementPayments);
      const statementNet = statementReceiptsValue - statementPaymentsValue;
      const difference = statementNet - system.net;
      return {
        account,
        system,
        rec,
        statementReceiptsValue,
        statementPaymentsValue,
        statementNet,
        difference,
      };
    });
  }, [bankAccounts, monthlySystemByAccount, reconciliationByAccount]);

  useEffect(() => {
    if (selectedAccountId) return;
    const first = rows[0];
    if (!first?.account?.id) return;
    setSelectedAccountId(String(first.account.id));
  }, [rows, selectedAccountId]);

  useEffect(() => {
    const accountId = String(selectedAccountId || '').trim();
    const accountChanged = lastSelectedAccountRef.current !== accountId;
    lastSelectedAccountRef.current = accountId;
    if (!accountId) {
      setReconciliationNotes('');
      setOpeningBalance('');
      setOpeningBalanceTouched(false);
      setStatementClosingBalance('');
      setStatementToDate(todayYmd());
      setStatementRows([]);
      setManualMatches({});
      return;
    }
    const rec = reconciliationByAccount.get(accountId);
    setReconciliationNotes(String(rec?.notes || ''));
    const recMatches = ((rec as any)?.draftManualMatches || {}) as Record<string, string>;
    setManualMatches(rec ? recMatches : {});
    if (rec && Array.isArray((rec as any)?.draftStatementRows)) {
      setStatementRows(((rec as any).draftStatementRows || []) as any);
    }
    if (rec) {
      if (accountChanged) {
        setOpeningBalance(String(toAmount((rec as any).openingBalance)));
        setOpeningBalanceTouched(false);
      }
      setStatementClosingBalance(String(toAmount((rec as any).statementClosingBalance)));
      setStatementToDate(String((rec as any)?.statementToDate || todayYmd()));
    } else if (accountChanged) {
      setOpeningBalance('');
      setOpeningBalanceTouched(false);
      setStatementClosingBalance('');
      setStatementToDate(todayYmd());
    }
    if (accountChanged) {
      const latestDraft = reconciliations
        .filter((row) => String(row.bankAccountId || '').trim() === accountId)
        .filter((row) => !isReconciliationDone(row))
        .slice()
        .sort((a, b) => toMs((b as any)?.updatedAt) - toMs((a as any)?.updatedAt))[0];
      const lastReconciled = reconciliations
        .filter((row) => String(row.bankAccountId || '').trim() === accountId)
        .filter((row) => isReconciliationDone(row))
        .slice()
        .sort((a, b) => startOfDayMs(String(b.rangeEndDate || '')) - startOfDayMs(String(a.rangeEndDate || '')))[0];
      if (latestDraft?.rangeStartDate && latestDraft?.rangeEndDate) {
        const draftStart = String((latestDraft as any).rangeStartDate || '');
        const draftEnd = String((latestDraft as any).rangeEndDate || '');
        setRangeStart(draftStart);
        setRangeEnd(draftEnd);
        setStatementToDate(String((latestDraft as any)?.statementToDate || draftEnd || todayYmd()));
        setOpeningBalance(String(toAmount((latestDraft as any)?.openingBalance || 0)));
        setStatementClosingBalance(String(toAmount((latestDraft as any)?.statementClosingBalance || 0)));
        setReconciliationNotes(String((latestDraft as any)?.notes || ''));
        setManualMatches((((latestDraft as any)?.draftManualMatches || {}) as Record<string, string>) || {});
        setStatementRows((((latestDraft as any)?.draftStatementRows || []) as any) || []);
      } else if (lastReconciled?.rangeEndDate) {
        const nextFrom = addDays(String(lastReconciled.rangeEndDate), 1);
        setRangeStart(nextFrom);
        setRangeEnd(nextFrom);
        setStatementToDate(nextFrom);
        setOpeningBalance(String(toAmount((lastReconciled as any)?.statementClosingBalance || 0)));
      } else {
        setRangeStart(FIRST_RECON_START_DATE);
        setRangeEnd(todayYmd());
      }
      setStatementRows([]);
      setCreateDialogRow(null);
      setCreateDialogPartyName('');
      setCreateDialogContraName('');
    }
  }, [reconciliationByAccount, reconciliations, selectedAccountId]);

  const lastFinalizedForSelected = useMemo(() => {
    const accountId = String(selectedAccountId || '').trim();
    if (!accountId) return null as ReconciliationDoc | null;
    return (
      reconciliations
        .filter((row) => String(row.bankAccountId || '').trim() === accountId)
        .filter((row) => !!(row as any)?.finalized || !!(row as any)?.reconciledAt)
        .slice()
        .sort((a, b) => {
          const ae = startOfDayMs(String(a.rangeEndDate || ''));
          const be = startOfDayMs(String(b.rangeEndDate || ''));
          return be - ae;
        })[0] || null
    );
  }, [reconciliations, selectedAccountId]);

  const nextAllowedStartForSelected = useMemo(() => {
    const end = String((lastFinalizedForSelected as any)?.rangeEndDate || '').trim();
    if (!end) return '';
    return addDays(end, 1);
  }, [lastFinalizedForSelected]);

  const lastSavedForSelected = useMemo(() => {
    const accountId = String(selectedAccountId || '').trim();
    if (!accountId) return null as ReconciliationDoc | null;
    return (
      reconciliations
        .filter((row) => String(row.bankAccountId || '').trim() === accountId)
        .slice()
        .sort((a, b) => {
          const au = toMs((a as any)?.updatedAt);
          const bu = toMs((b as any)?.updatedAt);
          if (bu !== au) return bu - au;
          const ae = startOfDayMs(String(a.rangeEndDate || ''));
          const be = startOfDayMs(String(b.rangeEndDate || ''));
          return be - ae;
        })[0] || null
    );
  }, [reconciliations, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId) return;
    if (openingBalanceTouched) return;
    if (!lastFinalizedForSelected) return;
    const prevClose = toAmount((lastFinalizedForSelected as any)?.statementClosingBalance);
    setOpeningBalance(prevClose ? String(prevClose) : '');
  }, [lastFinalizedForSelected, openingBalanceTouched, selectedAccountId]);

  const openingFromTransactions = useMemo(() => {
    const accountId = String(selectedAccountId || '').trim();
    if (!accountId || !rangeStart) return { value: 0, missingDateCount: 0 };
    let value = 0;
    let missingDateCount = 0;
    const startMs = startOfDayMs(rangeStart);
    transactions.forEach((row) => {
      if (String(row.bankAccountId || '').trim() !== accountId) return;
      const ms = toMs(row.txnDate);
      if (!ms) {
        missingDateCount += 1;
        return;
      }
      if (ms >= startMs) return;
      const amt = toAmount(row.amount);
      const type = String(row.txnType || 'receipt').toLowerCase();
      value += type === 'payment' ? -amt : amt;
    });
    return { value, missingDateCount };
  }, [rangeStart, selectedAccountId, transactions]);

  useEffect(() => {
    if (!selectedAccountId) return;
    if (openingBalanceTouched) return;
    if (lastFinalizedForSelected) return;
    setOpeningBalance(String(openingFromTransactions.value || 0));
  }, [lastFinalizedForSelected, openingBalanceTouched, openingFromTransactions.value, selectedAccountId]);

  const selectedRow = useMemo(
    () => rows.find((row) => String(row.account.id || '') === String(selectedAccountId || '')) || null,
    [rows, selectedAccountId]
  );
  const selectedHistory = useMemo(() => {
    const accountId = String(selectedAccountId || '').trim();
    if (!accountId) return [] as ReconciliationDoc[];
    return reconciliations
      .filter((row) => String(row.bankAccountId || '').trim() === accountId)
      .slice()
      .sort((a, b) => toMs(b.updatedAt) - toMs(a.updatedAt));
  }, [reconciliations, selectedAccountId]);

  const activeDraftForSelected = useMemo(
    () => selectedHistory.find((row) => !isReconciliationDone(row)) || null,
    [selectedHistory]
  );

  const isDraftRangeSelected = useMemo(() => {
    if (!activeDraftForSelected) return true;
    const draftKey = String((activeDraftForSelected as any)?.rangeKey || '').trim();
    if (draftKey) return draftKey === selectedRangeKey;
    return (
      String((activeDraftForSelected as any)?.rangeStartDate || '') === String(rangeStart || '') &&
      String((activeDraftForSelected as any)?.rangeEndDate || '') === String(rangeEnd || '')
    );
  }, [activeDraftForSelected, rangeEnd, rangeStart, selectedRangeKey]);

  const draftBlocked = Boolean(activeDraftForSelected) && !isDraftRangeSelected;

  const continueDraft = () => {
    if (!activeDraftForSelected) return;
    const draftStart = String((activeDraftForSelected as any)?.rangeStartDate || '');
    const draftEnd = String((activeDraftForSelected as any)?.rangeEndDate || '');
    if (!draftStart || !draftEnd) return;
    setRangeStart(draftStart);
    setRangeEnd(draftEnd);
    setStatementToDate(String((activeDraftForSelected as any)?.statementToDate || draftEnd));
    setOpeningBalance(String(toAmount((activeDraftForSelected as any)?.openingBalance || 0)));
    setStatementClosingBalance(String(toAmount((activeDraftForSelected as any)?.statementClosingBalance || 0)));
    setReconciliationNotes(String((activeDraftForSelected as any)?.notes || ''));
    setManualMatches((((activeDraftForSelected as any)?.draftManualMatches || {}) as Record<string, string>) || {});
    setStatementRows((((activeDraftForSelected as any)?.draftStatementRows || []) as any) || []);
  };

  const partyNameOptions = useMemo(() => {
    const values = new Set<string>();
    transactions.forEach((row) => {
      const value = String((row as any)?.partyName || '').trim();
      if (value) values.add(value);
    });
    customPartyNames.forEach((value) => {
      const v = String(value || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [customPartyNames, transactions]);

  const bankAccountByLast4 = useMemo(() => {
    const map = new Map<string, BankAccount>();
    bankAccounts.forEach((bank) => {
      const key = accountLast4FromBank(bank);
      if (key) map.set(key, bank);
    });
    return map;
  }, [bankAccounts]);

  const detectStatementAccount = (text: string, fileName: string): StatementAccountDetection | null => {
    const candidates = new Map<string, number>();
    const addScore = (last4: string, score: number) => {
      const key = String(last4 || '').trim().slice(-4);
      if (!key || key.length !== 4) return;
      if (!bankAccountByLast4.has(key)) return;
      candidates.set(key, (candidates.get(key) || 0) + score);
    };

    const fullText = String(text || '').slice(0, 50000);
    const txnHeaderIndex = fullText.search(/Txn Date\s*\t/i);
    const headerText = txnHeaderIndex >= 0 ? fullText.slice(0, txnHeaderIndex) : fullText;
    const nameText = String(fileName || '').slice(0, 500);

    const accountNumberHeaderPatterns: RegExp[] = [
      /account\s*number[^0-9]{0,40}([0-9]{6,18})/gi,
      /\bA\/C(?:OUNT)?\s*NUMBER[^0-9]{0,40}([0-9]{6,18})/gi,
      /\bA\/C\s*NO\.?[^0-9]{0,40}([0-9]{6,18})/gi,
    ];
    accountNumberHeaderPatterns.forEach((re) => {
      re.lastIndex = 0;
      for (;;) {
        const m = re.exec(headerText);
        if (!m) break;
        const digits = String(m[1] || '').trim();
        if (digits.length >= 6) addScore(digits.slice(-4), 30);
      }
    });

    const accountPatterns: RegExp[] = [
      /account[^0-9]{0,30}([0-9]{6,18})/gi,
      /\bA\/C\b[^0-9]{0,30}([0-9]{6,18})/gi,
      /\bACCT\b[^0-9]{0,30}([0-9]{6,18})/gi,
      /\bA\/C(?:OUNT)?\s*(?:NO\.?|NUMBER)?[^0-9]{0,30}([0-9]{6,18})/gi,
      /\b(?:for\s+)?a\/c\b[^0-9]{0,30}([0-9]{6,18})/gi,
    ];
    accountPatterns.forEach((re) => {
      re.lastIndex = 0;
      for (;;) {
        const m = re.exec(headerText);
        if (!m) break;
        const digits = String(m[1] || '').trim();
        if (digits.length >= 6) addScore(digits.slice(-4), 6);
      }
    });

    const maskedRe = /x{2,}\s*([0-9]{4})/gi;
    maskedRe.lastIndex = 0;
    for (;;) {
      const m = maskedRe.exec(headerText);
      if (!m) break;
      addScore(String(m[1] || '').trim(), 3);
    }

    const longDigits = /([0-9]{10,18})/g;
    longDigits.lastIndex = 0;
    for (;;) {
      const m = longDigits.exec(headerText);
      if (!m) break;
      const digits = String(m[1] || '').trim();
      addScore(digits.slice(-4), 1);
    }

    const fileDigits = /([0-9]{4})/g;
    fileDigits.lastIndex = 0;
    for (;;) {
      const m = fileDigits.exec(nameText);
      if (!m) break;
      addScore(String(m[1] || '').trim(), 2);
    }

    const ranked = Array.from(candidates.entries())
      .map(([last4, score]) => ({ last4, score }))
      .sort((a, b) => b.score - a.score);
    const top = ranked[0];
    if (!top) return null;
    const second = ranked[1];
    if (second && second.score === top.score && second.last4 !== top.last4) return null;
    const bank = bankAccountByLast4.get(top.last4);
    if (!bank) return null;
    return { bank, last4: top.last4 };
  };

  const unreconciledCandidates = useMemo(() => {
    const accountId = String(selectedAccountId || '').trim();
    if (!accountId) return [] as Array<{
      id: string;
      type: 'receipt' | 'payment';
      amount: number;
      hasTxnDate: boolean;
      dateYmd: string;
      refNo: string;
      notes: string;
      raw: BankTransaction;
    }>;
    return transactions
      .filter((row) => String(row.bankAccountId || '').trim() === accountId)
      .filter((row) => !isTxnReconciled(row))
      .map((row) => {
        const ms = toMs((row as any)?.txnDate);
        return {
          id: String(row.id),
          type: String(row.txnType || 'receipt').toLowerCase() === 'payment' ? 'payment' : 'receipt',
          amount: toAmount(row.amount),
          hasTxnDate: !!ms,
          dateYmd: ms ? toYmd(new Date(ms)) : '',
          refNo: String((row as any).referenceNo || '').trim(),
          notes: String((row as any).notes || '').trim(),
          raw: row,
        };
      });
  }, [selectedAccountId, transactions]);

  const importStatementText = useCallback(async (ext: string, text: string) => {
    if (nextAllowedStartForSelected && rangeStart < nextAllowedStartForSelected) {
      setError(
        `Cannot import statement. Last reconciliation is till ${String(
          (lastFinalizedForSelected as any)?.rangeEndDate || ''
        )}. Next reconciliation must start from ${nextAllowedStartForSelected}.`
      );
      return;
    }
    setError(null);
    try {
      let parsed: Array<{ dateYmd: string; description: string; debit: number; credit: number; refNo: string }> = [];
      let statementDateFromFile = '';
      let closingBalanceFromFile: number | null = null;
      if (ext === 'xls' || String(text || '').includes('Txn Date\tValue Date')) {
        const result = parseStatementRowsFromTsv(text);
        parsed = result.rows;
        statementDateFromFile = String(result.statementDate || '');
        closingBalanceFromFile = result.closingBalance;
      } else {
        const rows = parseCsvRows(text);
        if (rows.length < 2) throw new Error('Statement CSV has no rows.');
        const header = rows[0].map((h) => String(h || '').trim().toLowerCase());
        const indexBy = (keys: string[]) => {
          for (let i = 0; i < header.length; i += 1) {
            if (keys.includes(header[i])) return i;
          }
          return -1;
        };
        const dateIdx = indexBy(['date', 'txn date', 'transaction date', 'value date']);
        const descIdx = indexBy(['description', 'narration', 'particulars', 'remarks']);
        const debitIdx = indexBy(['debit', 'withdraw', 'withdrawal', 'dr']);
        const creditIdx = indexBy(['credit', 'deposit', 'receipt', 'cr']);
        const refIdx = indexBy(['ref', 'reference', 'chq', 'cheque', 'ref no', 'ref / chq no']);
        const balanceIdx = indexBy(['balance', 'running balance', 'closing balance', 'avail balance', 'available balance']);
        if (dateIdx < 0 || descIdx < 0 || (debitIdx < 0 && creditIdx < 0)) {
          throw new Error('File must have Date, Description and Debit/Credit columns.');
        }
        const parsedWithBalance = rows
          .slice(1)
          .map((r) => {
            const dateYmd = parseStatementDate(String(r[dateIdx] || ''));
            const description = String(r[descIdx] || '').trim();
            const debit = debitIdx >= 0 ? toAmount(String(r[debitIdx] || '').replace(/[,₹\s]/g, '')) : 0;
            const credit = creditIdx >= 0 ? toAmount(String(r[creditIdx] || '').replace(/[,₹\s]/g, '')) : 0;
            const refNo = refIdx >= 0 ? String(r[refIdx] || '').trim() : '';
            const balance = balanceIdx >= 0 ? parseBalanceToken(String(r[balanceIdx] || '')) : null;
            return { dateYmd, description, debit, credit, refNo, balance };
          })
          .filter((r) => r.dateYmd && (r.debit > 0 || r.credit > 0));
        parsed = parsedWithBalance.map(({ dateYmd, description, debit, credit, refNo }) => ({
          dateYmd,
          description,
          debit,
          credit,
          refNo,
        }));
        const lastWithBalance = [...parsedWithBalance]
          .reverse()
          .find((row) => row.balance !== null && Number.isFinite(Number(row.balance)));
        if (lastWithBalance) {
          closingBalanceFromFile = Number(lastWithBalance.balance);
        }
      }
      if (!parsed.length) throw new Error('No valid statement transactions found.');
      const minDate = parsed
        .map((r) => String(r.dateYmd || '').trim())
        .filter(Boolean)
        .sort((a, b) => startOfDayMs(a) - startOfDayMs(b))[0];
      if (nextAllowedStartForSelected && minDate && minDate < nextAllowedStartForSelected) {
        throw new Error(
          `Statement has transactions from ${minDate}. Last reconciliation is till ${String(
            (lastFinalizedForSelected as any)?.rangeEndDate || ''
          )}. Import only from ${nextAllowedStartForSelected} onward.`
        );
      }
      setStatementRows(parsed);
      const maxDate = parsed
        .map((r) => r.dateYmd)
        .sort((a, b) => startOfDayMs(b) - startOfDayMs(a))[0];
      const nextStatementDate = statementDateFromFile || maxDate || '';
      if (nextStatementDate) {
        setStatementToDate(nextStatementDate);
        setRangeEnd(nextStatementDate);
      }
      if (closingBalanceFromFile !== null && Number.isFinite(closingBalanceFromFile)) {
        setStatementClosingBalance(String(closingBalanceFromFile));
      }
      setManualMatches({});
      setCreateDialogRow(null);
      setCreateDialogPartyName('');
      setCreateDialogContraName('');
    } catch (err: any) {
      setError(err?.message || 'Failed to import statement CSV.');
      setStatementRows([]);
      setManualMatches({});
      setCreateDialogRow(null);
      setCreateDialogPartyName('');
      setCreateDialogContraName('');
    }
  }, [
    lastFinalizedForSelected,
    nextAllowedStartForSelected,
    rangeStart,
    setCreateDialogContraName,
    setCreateDialogPartyName,
    setCreateDialogRow,
    setError,
    setManualMatches,
    setRangeEnd,
    setStatementClosingBalance,
    setStatementRows,
    setStatementToDate,
  ]);

  useEffect(() => {
    if (!continueImportAfterSwitch) return;
    if (!pendingStatementSwitch) return;
    const targetId = String(pendingStatementSwitch.detected.bank.id || '').trim();
    if (!targetId) return;
    if (String(selectedAccountId || '').trim() !== targetId) return;
    setContinueImportAfterSwitch(false);
    setStatementSwitchDialogOpen(false);
    const { ext, text } = pendingStatementSwitch;
    setPendingStatementSwitch(null);
    // Continue import after account switch using already-read statement text.
    void importStatementText(ext, text);
  }, [continueImportAfterSwitch, importStatementText, pendingStatementSwitch, selectedAccountId]);

  const statementCandidates = useMemo(() => {
    return statementRows.map((srow) => {
      const stType: 'receipt' | 'payment' = srow.credit > 0 ? 'receipt' : 'payment';
      const stAmt = srow.credit > 0 ? srow.credit : srow.debit;
      const stAmtPaise = toPaise(stAmt);
      return unreconciledCandidates
        .filter((t) => t.type === stType)
        .filter((t) => toPaise(t.amount) === stAmtPaise)
        .map((c) => {
          const amountDiff = Math.abs(c.amount - stAmt);
          const dateDiff =
            c.dateYmd && srow.dateYmd
              ? Math.abs(startOfDayMs(c.dateYmd) - startOfDayMs(srow.dateYmd)) / (24 * 60 * 60 * 1000)
              : 99;
          const refScore =
            srow.refNo && c.refNo ? (srow.refNo.toLowerCase() === c.refNo.toLowerCase() ? 0 : 2) : 1;
          const score = amountDiff * 10 + dateDiff + refScore;
          return { ...c, score };
        })
        .sort((a, b) => a.score - b.score)
        .slice(0, 20);
    });
  }, [statementRows, unreconciledCandidates]);

  const selectedMatchIds = useMemo(() => {
    const s = new Set<string>();
    Object.values(manualMatches).forEach((id) => {
      if (id) s.add(id);
    });
    return s;
  }, [manualMatches]);

  const matchedSelectionCount = useMemo(
    () => Object.values(manualMatches).filter((id) => String(id || '').trim()).length,
    [manualMatches]
  );

  const statementTotals = useMemo(() => {
    return statementRows.reduce(
      (acc, row) => {
        acc.receipts += row.credit > 0 ? row.credit : 0;
        acc.payments += row.debit > 0 ? row.debit : 0;
        return acc;
      },
      { receipts: 0, payments: 0 }
    );
  }, [statementRows]);

  const saveDraft = async () => {
    if (nextAllowedStartForSelected && rangeStart < nextAllowedStartForSelected) {
      setError(
        `Invalid start date. Last reconciliation is till ${String(
          (lastFinalizedForSelected as any)?.rangeEndDate || ''
        )}. Next reconciliation must start from ${nextAllowedStartForSelected}.`
      );
      return;
    }
    if (draftBlocked) {
      setError('An older draft exists for this bank. Click Continue Draft and complete it first.');
      return;
    }
    if (!currentProjectId) {
      setError('Select project first.');
      return;
    }
    if (!selectedRow?.account?.id) {
      setError('Select a bank account row first.');
      return;
    }
    if (!currentUser?.email) {
      setError('Not signed in.');
      return;
    }
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      setError('Select a valid date range.');
      return;
    }
    const receiptValue = toAmount(statementTotals.receipts);
    const paymentValue = toAmount(statementTotals.payments);
    const openingValue = toAmount(openingBalance);
    const closingValue = toAmount(statementClosingBalance);
    setSaving(true);
    setError(null);
    try {
      const accountId = String(selectedRow.account.id || '').trim();
      const docId = `${accountId}__${selectedRangeKey}`;
      await setDoc(
        doc(db, 'projects', currentProjectId, 'bankReconciliations', docId),
        {
          bankAccountId: accountId,
          rangeKey: selectedRangeKey,
          rangeStartDate: rangeStart,
          rangeEndDate: rangeEnd,
          monthKey: selectedIsFullMonth ? selectedMonthKey : null,
          statementReceipts: receiptValue,
          statementPayments: paymentValue,
          openingBalance: openingValue,
          statementClosingBalance: closingValue,
          statementToDate: statementToDate || null,
          statementImportedCount: statementRows.length,
          statementMatchedCount: matchedSelectionCount,
          draftManualMatches: manualMatches,
          draftStatementRows: statementRows,
          notes: String(reconciliationNotes || '').trim() || null,
          finalized: false,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        },
        { merge: true }
      );
    } catch (err: any) {
      console.error('Save reconciliation failed', err);
      setError(err?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  const saveReconciliation = async () => {
    if (nextAllowedStartForSelected && rangeStart < nextAllowedStartForSelected) {
      setError(
        `Invalid start date. Last reconciliation is till ${String(
          (lastFinalizedForSelected as any)?.rangeEndDate || ''
        )}. Next reconciliation must start from ${nextAllowedStartForSelected}.`
      );
      return;
    }
    if (draftBlocked) {
      setError('An older draft exists for this bank. Click Continue Draft and complete it first.');
      return;
    }
    if (!currentProjectId || !selectedRow?.account?.id || !currentUser?.email) return;
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      setError('Select a valid date range.');
      return;
    }
    if (!statementRows.length) {
      setError('Import statement first.');
      return;
    }
    if (matchedSelectionCount !== statementRows.length) {
      setError('Match or create transaction for every statement row before Save Reconciliation.');
      return;
    }
    const accountId = String(selectedRow.account.id || '').trim();
    const receiptValue = toAmount(statementTotals.receipts);
    const paymentValue = toAmount(statementTotals.payments);
    const openingValue = toAmount(openingBalance);
    const closingValue = toAmount(statementClosingBalance);
    setSaving(true);
    setError(null);
    try {
      const pairs = Object.entries(manualMatches)
        .map(([rowIndex, txnId]) => ({ rowIndex: Number(rowIndex), txnId: String(txnId || '').trim() }))
        .filter((x) => x.txnId);
      const uniqueIds = new Set<string>();
      for (const p of pairs) {
        if (uniqueIds.has(p.txnId)) throw new Error('Same transaction selected multiple times.');
        uniqueIds.add(p.txnId);
      }
      const batch = writeBatch(db);
      pairs.forEach((p) => {
        const srow = statementRows[p.rowIndex];
        const txn = unreconciledCandidates.find((x) => x.id === p.txnId);
        if (!srow || !txn) return;
        const dref = doc(db, 'projects', currentProjectId, 'bankTransactions', txn.id);
        const nextNotes = [txn.notes, srow.description].filter(Boolean).join(' | ');
        const patch: any = {
          isReconciled: true,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          reconciledAt: serverTimestamp(),
          reconciliationRangeKey: selectedRangeKey,
          notes: nextNotes || null,
        };
        if (!(txn as any).hasTxnDate && srow.dateYmd) {
          patch.txnDate = Timestamp.fromDate(new Date(`${srow.dateYmd}T00:00:00`));
        }
        batch.set(dref, patch, { merge: true });
      });
      const recDocId = `${accountId}__${selectedRangeKey}`;
      batch.set(
        doc(db, 'projects', currentProjectId, 'bankReconciliations', recDocId),
        {
          bankAccountId: accountId,
          rangeKey: selectedRangeKey,
          rangeStartDate: rangeStart,
          rangeEndDate: rangeEnd,
          monthKey: selectedIsFullMonth ? selectedMonthKey : null,
          statementReceipts: receiptValue,
          statementPayments: paymentValue,
          openingBalance: openingValue,
          statementClosingBalance: closingValue,
          statementToDate: statementToDate || null,
          statementImportedCount: statementRows.length,
          statementMatchedCount: pairs.length,
          draftManualMatches: manualMatches,
          draftStatementRows: [],
          notes: String(reconciliationNotes || '').trim() || null,
          finalized: true,
          reconciledAt: serverTimestamp(),
          reconciledBy: currentUser.email,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        },
        { merge: true }
      );
      await batch.commit();
      setStatementRows([]);
      setManualMatches({});
      setCreateDialogRow(null);
      setCreateDialogPartyName('');
      setCreateDialogContraName('');
      const nextFrom = addDays(rangeEnd, 1);
      setRangeStart(nextFrom);
      setRangeEnd(nextFrom);
      setStatementToDate(nextFrom);
      setOpeningBalance(String(closingValue || 0));
    } catch (err: any) {
      setError(err?.message || 'Failed to save reconciliation.');
    } finally {
      setSaving(false);
    }
  };

  const undoLatestReconciliation = async () => {
    if (!currentProjectId || !selectedRow?.account?.id || !currentUser?.email) return;
    const latest = selectedHistory[0];
    if (!latest) return;
    const accountId = String(selectedRow.account.id || '').trim();
    const targetRangeKey = String((latest as any)?.rangeKey || '').trim();
    const targetStart = String((latest as any)?.rangeStartDate || '').trim();
    const targetEnd = String((latest as any)?.rangeEndDate || '').trim();
    const fromMs = startOfDayMs(targetStart);
    const toMsValue = endOfDayMs(targetEnd);
    const matched = transactions.filter((row) => {
      if (String((row as any)?.bankAccountId || '').trim() !== accountId) return false;
      if ((row as any)?.isReconciled !== true) return false;
      const key = String((row as any)?.reconciliationRangeKey || '').trim();
      if (key && targetRangeKey && key === targetRangeKey) return true;
      const txnMs = toMs((row as any)?.txnDate) || toMs((row as any)?.createdAt);
      if (!txnMs) return false;
      return txnMs >= fromMs && txnMs <= toMsValue;
    });
    if (!matched.length) {
      setError(`No reconciled transactions found in latest reconciliation range ${targetStart} to ${targetEnd}.`);
      return;
    }
    setUndoing(true);
    setError(null);
    try {
      const chunkSize = 450;
      for (let i = 0; i < matched.length; i += chunkSize) {
        const chunk = matched.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        chunk.forEach((row) => {
          const txRef = doc(db, 'projects', currentProjectId, 'bankTransactions', String(row.id));
          batch.set(
            txRef,
            {
              isReconciled: false,
              reconciliationRangeKey: null,
              reconciledAt: null,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser.email,
            },
            { merge: true }
          );
        });
        await batch.commit();
      }

      const recDocId = `${accountId}__${targetRangeKey || `${targetStart}__${targetEnd}`}`;
      await setDoc(
        doc(db, 'projects', currentProjectId, 'bankReconciliations', recDocId),
        {
          bankAccountId: accountId,
          rangeKey: targetRangeKey || null,
          rangeStartDate: targetStart || null,
          rangeEndDate: targetEnd || null,
          monthKey: selectedIsFullMonth ? selectedMonthKey : null,
          statementMatchedCount: 0,
          finalized: false,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          undoneAt: serverTimestamp(),
          undoneBy: currentUser.email,
        },
        { merge: true }
      );
      setManualMatches({});
    } catch (err: any) {
      setError(err?.message || 'Failed to undo reconciliation.');
    } finally {
      setUndoing(false);
    }
  };

  const overlapCountForSelected = useMemo(() => {
    if (!selectedAccountId || !rangeStart || !rangeEnd) return 0;
    return reconciliations.filter((row) => {
      if (String(row.bankAccountId || '') !== String(selectedAccountId)) return false;
      const key = String(row.rangeKey || '').trim();
      if (key && key === selectedRangeKey) return false;
      const rowStart = String(row.rangeStartDate || '').trim();
      const rowEnd = String(row.rangeEndDate || '').trim();
      if (!rowStart || !rowEnd) return false;
      return rowStart <= rangeEnd && rowEnd >= rangeStart;
    }).length;
  }, [rangeEnd, rangeStart, reconciliations, selectedAccountId, selectedRangeKey]);

  const importStatementFile = async (file: File | null) => {
    if (!file) return;
    const ext = String(file.name || '').split('.').pop()?.toLowerCase() || '';
    if (ext === 'txt') {
      setError('TXT statements are not supported. Please import CSV or XLS.');
      return;
    }
    try {
      const text = await file.text();
      const detected = detectStatementAccount(text, String(file.name || ''));
      const selectedId = String(selectedAccountId || '').trim();
      const detectedId = String(detected?.bank?.id || '').trim();
      if (detected && detectedId && selectedId && detectedId !== selectedId) {
        setPendingStatementSwitch({ ext, text, fileName: String(file.name || ''), detected });
        setStatementSwitchDialogOpen(true);
        return;
      }
      await importStatementText(ext, text);
    } catch (err: any) {
      setError(err?.message || 'Failed to import statement.');
    }
  };

  const createTransactionFromStatementRow = async (
    rowIndex: number,
    partyNameInput: string,
    contraAccountNameInput: string
  ) => {
    if (!currentProjectId || !currentUser?.email) return;
    const accountId = String(selectedRow?.account?.id || '').trim();
    if (!accountId) return;
    const srow = statementRows[rowIndex];
    if (!srow) return;
    const selectedPartyName = String(partyNameInput || '').trim();
    if (!selectedPartyName) {
      setError('Select party/vendor name before creating transaction.');
      return;
    }
    setCustomPartyNames((prev) => {
      if (prev.some((p) => p.toLowerCase() === selectedPartyName.toLowerCase())) return prev;
      return [selectedPartyName, ...prev].slice(0, 200);
    });
    const amount = toAmount(srow.credit > 0 ? srow.credit : srow.debit);
    if (!amount) {
      setError('Cannot create transaction: statement row has zero amount.');
      return;
    }
    const txnType: 'receipt' | 'payment' = srow.credit > 0 ? 'receipt' : 'payment';
    const contraInfo = parseContraFromStatement(srow.description, srow.refNo);
    const targetBank = contraInfo.isContra ? bankAccountByLast4.get(contraInfo.last4) || null : null;
    const targetBankId = String(targetBank?.id || '').trim();
    const currentAccountName = String(selectedRow?.account?.name || '').trim();
    const linkedContraName = String(targetBank?.name || targetBank?.bankName || '').trim();
    const manualContraName = String(contraAccountNameInput || `A/C ${contraInfo.accountNo || 'Unknown'}`).trim();
    const effectiveContraName = linkedContraName || manualContraName;
    const contraGroupId = `contra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setCreatingMatchRow(rowIndex);
    setError(null);
    try {
      const txRef = doc(collection(db, 'projects', currentProjectId, 'bankTransactions'));
      const payload: any = {
        bankAccountId: accountId,
        txnType,
        amount,
        referenceNo: String(srow.refNo || '').trim() || null,
        partyName: contraInfo.isContra ? (effectiveContraName || null) : (selectedPartyName || null),
        notes: String(srow.description || '').trim() || null,
        legacyChqDate: ymdToDdMmYyyy(srow.dateYmd),
        isContra: contraInfo.isContra || false,
        contraGroupId: contraInfo.isContra ? contraGroupId : null,
        contraAccountId: targetBankId || null,
        contraAccountName: contraInfo.isContra ? (effectiveContraName || null) : null,
        isReconciled: false,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      await setDoc(txRef, payload, { merge: true });

      if (contraInfo.isContra && targetBankId && targetBankId !== accountId) {
        const oppositeType: 'receipt' | 'payment' = txnType === 'receipt' ? 'payment' : 'receipt';
        const statementRefNo = String(srow.refNo || '').trim().toLowerCase();
        const existingOpposite = transactions.find((row) => {
          if (String((row as any)?.bankAccountId || '').trim() !== targetBankId) return false;
          if (isTxnReconciled(row)) return false;
          if (String((row as any)?.contraGroupId || '').trim()) return false;
          if (String((row as any)?.contraLinkedTxnId || '').trim()) return false;
          const rowType = String((row as any)?.txnType || '').toLowerCase();
          if (rowType !== oppositeType) return false;
          if (Math.abs(toAmount((row as any)?.amount) - amount) > 0.01) return false;
          const rowRefNo = String((row as any)?.referenceNo || '').trim().toLowerCase();
          if (statementRefNo && rowRefNo && rowRefNo !== statementRefNo) return false;
          const rowDateMs = toMs((row as any)?.txnDate);
          const rowDateYmd = rowDateMs ? toYmd(new Date(rowDateMs)) : '';
          return rowDateYmd === srow.dateYmd;
        });
        if (existingOpposite?.id) {
          await setDoc(
            doc(db, 'projects', currentProjectId, 'bankTransactions', String(existingOpposite.id)),
            {
              isContra: true,
              contraGroupId,
              contraLinkedTxnId: txRef.id,
              contraAccountId: accountId,
              contraAccountName: currentAccountName || null,
              partyName: currentAccountName || null,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser.email,
            },
            { merge: true }
          );
        } else {
          const oppositeRef = doc(collection(db, 'projects', currentProjectId, 'bankTransactions'));
          const oppositePayload: any = {
            bankAccountId: targetBankId,
            txnType: oppositeType,
            amount,
            referenceNo: String(srow.refNo || '').trim() || null,
            partyName: currentAccountName || null,
            notes: String(srow.description || '').trim() || null,
            legacyChqDate: ymdToDdMmYyyy(srow.dateYmd),
            isReconciled: false,
            isContra: true,
            contraGroupId,
            contraLinkedTxnId: txRef.id,
            contraAccountId: accountId,
            contraAccountName: currentAccountName || null,
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser.email,
          };
          if (srow.dateYmd) {
            oppositePayload.txnDate = Timestamp.fromDate(new Date(`${srow.dateYmd}T00:00:00`));
          }
          await setDoc(oppositeRef, oppositePayload, { merge: true });
        }
      }

      setTransactions((prev) => [
        {
          id: txRef.id,
          bankAccountId: accountId,
          txnType,
          amount,
          referenceNo: payload.referenceNo,
          partyName: payload.partyName,
          notes: payload.notes,
          legacyChqDate: payload.legacyChqDate,
          isReconciled: false,
          isContra: payload.isContra,
          contraGroupId: payload.contraGroupId,
          contraAccountId: payload.contraAccountId,
          contraAccountName: payload.contraAccountName,
          txnDate: null,
          createdAt: new Date(),
        },
        ...prev,
      ]);
      setManualMatches((prev) => ({ ...prev, [rowIndex]: txRef.id }));
      setCreateDialogRow(null);
      setCreateDialogPartyName('');
      setCreateDialogContraName('');
    } catch (err: any) {
      setError(err?.message || 'Failed to create transaction from statement row.');
    } finally {
      setCreatingMatchRow(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, height: '100%', overflow: 'auto' }}>
      <Stack spacing={1.5}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Bank Reconciliation
        </Typography>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {bankHeaderCards.map((card) => (
            <Paper
              key={card.id}
              variant="outlined"
              onClick={() => setSelectedAccountId(card.id)}
              sx={{
                p: 1.25,
                minWidth: 260,
                borderRadius: 1.5,
                borderColor: String(selectedAccountId || '') === card.id ? 'primary.main' : 'divider',
                background:
                  'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(76,175,80,0.05) 100%)',
                cursor: 'pointer',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {card.name}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
                Current Balance
              </Typography>
              <Typography sx={{ fontSize: 22, lineHeight: 1.1, fontWeight: 900, color: '#0d47a1' }}>
                ₹{toAmount(card.currentBalance).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                Reconciled: ₹{toAmount(card.reconciledBalance).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Reconciliation Date: {card.reconciledDate || '—'}
              </Typography>
            </Paper>
          ))}
        </Stack>
        {rangeStart > rangeEnd ? <Alert severity="warning">From Date must be before Statement Date.</Alert> : null}

        {error ? <Alert severity="error">{error}</Alert> : null}
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading reconciliation data…</Typography>
          </Stack>
        ) : null}

        {selectedRow ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: 2,
              background: 'linear-gradient(180deg, rgba(2,136,209,0.03) 0%, rgba(255,255,255,1) 35%)',
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'primary.dark' }}>
                    Reconcile: {String(selectedRow.account.name || selectedRow.account.bankName || selectedRow.account.id)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`Imported ${statementRows.length}`}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Matched ${matchedSelectionCount}`}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    color={Math.max(0, statementRows.length - matchedSelectionCount) > 0 ? 'warning' : 'success'}
                    variant="outlined"
                    label={`Unmatched ${Math.max(0, statementRows.length - matchedSelectionCount)}`}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </Box>
              <Box sx={{ py: 0.25 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Last Reconciliation {lastFinalizedForSelected ? '(Finalized)' : '(Latest Saved)'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Date:{' '}
                    {String((lastFinalizedForSelected || lastSavedForSelected)?.rangeEndDate || '—')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Closing: ₹
                    {toAmount(((lastFinalizedForSelected || lastSavedForSelected) as any)?.statementClosingBalance).toLocaleString('en-IN')}
                  </Typography>
                </Stack>
              </Box>
              {overlapCountForSelected > 0 ? (
                <Alert severity="info">
                  {overlapCountForSelected} overlapping reconciliation range(s) exist for this account. Current selected range is {rangeStart} to {rangeEnd}.
                </Alert>
              ) : null}
              {activeDraftForSelected ? (
                <Alert severity="warning" action={<Button size="small" onClick={continueDraft}>Continue Draft</Button>}>
                  Draft pending for {String((activeDraftForSelected as any)?.rangeStartDate || '—')} to{' '}
                  {String((activeDraftForSelected as any)?.rangeEndDate || '—')}. Complete this draft before starting a new statement.
                </Alert>
              ) : null}
              {!lastSavedForSelected ? (
                <Alert severity="info">
                  No prior reconciliation found. Opening balance is auto-calculated from transactions before {rangeStart}.
                </Alert>
              ) : null}
              {!lastSavedForSelected && openingFromTransactions.missingDateCount > 0 ? (
                <Alert severity="warning">
                  {openingFromTransactions.missingDateCount} transactions have no transaction date and are excluded from opening balance.
                </Alert>
              ) : null}
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
                <Box sx={{ flex: '1 1 50%', maxWidth: { lg: '50%' }, minWidth: 0 }}>
                  <Stack spacing={1}>
                    <Box
                      onDragEnter={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setStatementDropActive(true);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setStatementDropActive(true);
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setStatementDropActive(false);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setStatementDropActive(false);
                        if (draftBlocked) return;
                        const file = event.dataTransfer?.files?.[0] || null;
                        importStatementFile(file);
                      }}
                      sx={{
                        border: '1px dashed',
                        borderColor: statementDropActive ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: { xs: 2, md: 2.5 },
                        bgcolor: statementDropActive ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.03)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <Stack spacing={1.25} alignItems="center" textAlign="center">
                        <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                        <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                          Drag or Drop Statement File
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supported: `.csv`, `.xls`
                        </Typography>
                        <Button
                          component="label"
                          size="small"
                          variant="contained"
                          disabled={draftBlocked}
                          startIcon={<UploadFileOutlinedIcon fontSize="small" />}
                        >
                          Upload Statement
                          <input
                            hidden
                            type="file"
                            accept=".csv,.xls,text/csv,application/vnd.ms-excel"
                            onChange={(e) => importStatementFile(e.target.files?.[0] || null)}
                          />
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ flex: '1 1 50%', maxWidth: { lg: '50%' }, minWidth: 0 }}>
                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <b>From:</b> {rangeStart || '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <b>To:</b> {statementToDate || '—'}
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <b>Opening:</b> ₹{toAmount(openingBalance).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <b>Closing:</b> ₹{toAmount(statementClosingBalance).toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        <b>Receipts:</b> ₹{toAmount(statementTotals.receipts).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                        <b>Payments:</b> ₹{toAmount(statementTotals.payments).toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                    <TextField
                      label="Notes"
                      size="small"
                      value={reconciliationNotes}
                      onChange={(e) => setReconciliationNotes(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fffdf7' } }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1 }} />
                      <Button variant="outlined" color="secondary" onClick={saveDraft} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Draft'}
                      </Button>
                      <Button variant="contained" color="primary" onClick={saveReconciliation} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Reconciliation'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
              {statementRows.length > 0 ? (
                <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack spacing={0.75}>
                    {statementRows.slice(0, 200).map((srow, idx) => {
                      const options = statementCandidates[idx] || [];
                      const selectedTxnId = String(manualMatches[idx] || '');
                      return (
                        <Stack
                          key={`${srow.dateYmd}_${idx}`}
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ md: 'center' }}
                          sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: srow.credit > 0 ? '#b7e4c7' : '#f8c7c7',
                            bgcolor: srow.credit > 0 ? '#f2fbf5' : '#fff5f5',
                          }}
                        >
                          <Stack spacing={0.25} sx={{ minWidth: { md: 520 } }}>
                            <Typography variant="caption">
                              <b>Transaction Date:</b> {srow.dateYmd || '—'}
                            </Typography>
                            <Typography variant="caption">
                              <b>Description:</b> {srow.description || '—'}
                            </Typography>
                            <Typography variant="caption">
                              <b>Ref No./Cheque No:</b> {srow.refNo || '—'}
                            </Typography>
                            <Typography variant="caption">
                              {toAmount(srow.debit) > 0 ? (
                                <>
                                  <b>Debit:</b> ₹{toAmount(srow.debit).toLocaleString('en-IN')}
                                </>
                              ) : (
                                <>
                                  <b>Credit:</b> ₹{toAmount(srow.credit).toLocaleString('en-IN')}
                                </>
                              )}
                            </Typography>
                          </Stack>
                          <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1 }} />
                          <TextField
                            size="small"
                            select
                            label="Match transaction"
                            value={selectedTxnId}
                            onChange={(e) => {
                              const nextValue = String(e.target.value || '');
                              if (nextValue === '__create__') {
                                const contraInfo = parseContraFromStatement(srow.description, srow.refNo);
                                const linkedContraBank = contraInfo.isContra
                                  ? bankAccountByLast4.get(contraInfo.last4)
                                  : null;
                                setCreateDialogRow(idx);
                                setCreateDialogPartyName(
                                  String(linkedContraBank?.name || linkedContraBank?.bankName || '').trim()
                                );
                                setCreateDialogContraName('');
                                return;
                              }
                              setCreateDialogRow((prev) => (prev === idx ? null : prev));
                              setManualMatches((prev) => ({ ...prev, [idx]: nextValue }));
                            }}
                            sx={{
                              minWidth: { xs: '100%', md: 340 },
                              ml: { md: 'auto' },
                              '& .MuiOutlinedInput-root': { bgcolor: 'white' },
                            }}
                            disabled={creatingMatchRow === idx}
                          >
                            <MenuItem value="">Not matched</MenuItem>
                            <MenuItem value="__create__">+ Create new transaction</MenuItem>
                            {options.map((opt) => {
                              const inUse = selectedMatchIds.has(opt.id) && selectedTxnId !== opt.id;
                              return (
                                <MenuItem key={opt.id} value={opt.id} disabled={inUse}>
                                  {opt.type.toUpperCase()} ₹{opt.amount.toLocaleString('en-IN')} • {opt.dateYmd || 'No Date'} • {opt.refNo || opt.notes || opt.id}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
              <Box sx={{ pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Reconciliation History
                </Typography>
                {selectedHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No history for this account.
                  </Typography>
                ) : (
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {selectedHistory.slice(0, 8).map((item) => {
                      const itemStart = String(item.rangeStartDate || '');
                      const itemEnd = String(item.rangeEndDate || '');
                      const labelRange = itemStart && itemEnd ? `${itemStart} to ${itemEnd}` : String(item.monthKey || 'Legacy');
                      const accountId = String(selectedRow?.account?.id || '').trim();
                      const itemRangeKey = String((item as any)?.rangeKey || '').trim();
                      const isReconciled = Boolean((item as any)?.finalized || (item as any)?.reconciledAt);
                      let matched = toAmount((item as any)?.statementMatchedCount);
                      const imported = toAmount((item as any)?.statementImportedCount);
                      const isLatest = selectedHistory[0]?.id === item.id;
                      if (isReconciled && matched === 0) {
                        matched = transactions.filter((txn) => {
                          if (String((txn as any)?.bankAccountId || '').trim() !== accountId) return false;
                          if (!isTxnReconciled(txn)) return false;
                          const txnRangeKey = String((txn as any)?.reconciliationRangeKey || '').trim();
                          if (itemRangeKey && txnRangeKey) return txnRangeKey === itemRangeKey;
                          const ms = toMs((txn as any)?.txnDate) || toMs((txn as any)?.createdAt);
                          if (!ms) return false;
                          return ms >= startOfDayMs(itemStart) && ms <= endOfDayMs(itemEnd);
                        }).length;
                      }
                      const historyTxns = transactions
                        .filter((txn) => {
                          if (String((txn as any)?.bankAccountId || '').trim() !== accountId) return false;
                          if (!isTxnReconciled(txn)) return false;
                          const txnRangeKey = String((txn as any)?.reconciliationRangeKey || '').trim();
                          if (itemRangeKey && txnRangeKey) return txnRangeKey === itemRangeKey;
                          const ms = toMs((txn as any)?.txnDate) || toMs((txn as any)?.createdAt);
                          if (!ms) return false;
                          return ms >= startOfDayMs(itemStart) && ms <= endOfDayMs(itemEnd);
                        })
                        .slice()
                        .sort((left, right) => {
                          const lm = toMs((left as any)?.txnDate) || toMs((left as any)?.createdAt);
                          const rm = toMs((right as any)?.txnDate) || toMs((right as any)?.createdAt);
                          return rm - lm;
                        });
                      const isExpanded = expandedHistoryId === item.id;
                      return (
                        <Stack key={item.id} spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'space-between' }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExpandedHistoryId((prev) => (prev === item.id ? null : item.id))}
                              sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {isExpanded ? '▼' : '▶'} {labelRange} • Matched {matched}/{imported || 0}
                              </Typography>
                            </Button>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                size="small"
                                label={isReconciled ? 'Reconciled' : 'Draft'}
                                color={isReconciled ? 'success' : 'default'}
                                variant={isReconciled ? 'filled' : 'outlined'}
                              />
                              {!isReconciled ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setRangeStart(itemStart);
                                    setRangeEnd(itemEnd);
                                    setStatementToDate(String((item as any)?.statementToDate || itemEnd));
                                    setOpeningBalance(String(toAmount((item as any)?.openingBalance || 0)));
                                    setStatementClosingBalance(String(toAmount((item as any)?.statementClosingBalance || 0)));
                                    setReconciliationNotes(String((item as any)?.notes || ''));
                                    setManualMatches((((item as any)?.draftManualMatches || {}) as Record<string, string>) || {});
                                    setStatementRows((((item as any)?.draftStatementRows || []) as any) || []);
                                  }}
                                >
                                  Continue
                                </Button>
                              ) : null}
                              {isLatest ? (
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  onClick={undoLatestReconciliation}
                                  disabled={undoing || saving || !isReconciled}
                                >
                                  {undoing ? 'Undoing…' : 'Undo'}
                                </Button>
                              ) : null}
                            </Stack>
                          </Stack>
                          {isExpanded ? (
                            <Box sx={{ pl: 2, pr: 0.5, py: 0.5, bgcolor: 'rgba(148,163,184,0.08)', borderRadius: 1 }}>
                              {historyTxns.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  No reconciled transactions found for this range.
                                </Typography>
                              ) : (
                                <Stack spacing={0.4}>
                                  {historyTxns.map((txn) => {
                                    const txnMs = toMs((txn as any)?.txnDate) || toMs((txn as any)?.createdAt);
                                    const txnDate = txnMs ? toYmd(new Date(txnMs)) : '—';
                                    const txnType = String((txn as any)?.txnType || 'receipt').toLowerCase() === 'payment' ? 'Payment' : 'Receipt';
                                    const amount = toAmount((txn as any)?.amount);
                                    const ref = String((txn as any)?.referenceNo || '').trim();
                                    const note = String((txn as any)?.notes || '').trim();
                                    return (
                                      <Typography key={String((txn as any)?.id || '')} variant="caption" color="text.secondary">
                                        {txnDate} • {txnType} • ₹{amount.toLocaleString('en-IN')} • {ref || note || '—'}
                                      </Typography>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Box>
                          ) : null}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
      <Dialog
        open={createDialogRow !== null}
        onClose={() => {
          if (creatingMatchRow !== null) return;
          setCreateDialogRow(null);
          setCreateDialogPartyName('');
          setCreateDialogContraName('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select Party / Vendor</DialogTitle>
        <DialogContent>
          {createDialogRow !== null ? (() => {
            const srow = statementRows[createDialogRow];
            if (!srow) return null;
            const info = parseContraFromStatement(srow.description, srow.refNo);
            if (!info.isContra) return null;
            const targetBank = bankAccountByLast4.get(info.last4);
            return (
              <Alert severity="info" sx={{ mb: 1 }}>
                Contra detected: {info.direction === 'from' ? 'Transfer From' : 'Transfer To'} {info.accountNo}
                {targetBank ? ` • Linked account: ${String(targetBank.name || targetBank.bankName || targetBank.id)}` : ''}
              </Alert>
            );
          })() : null}
          <Autocomplete
            freeSolo
            options={partyNameOptions}
            inputValue={createDialogPartyName}
            onInputChange={(_, value) => setCreateDialogPartyName(String(value || ''))}
            onChange={(_, value) =>
              setCreateDialogPartyName(typeof value === 'string' ? value : String(value || ''))
            }
            renderInput={(params) => (
              <TextField {...params} autoFocus margin="dense" label="Party / Vendor" fullWidth />
            )}
          />
          {createDialogRow !== null ? (() => {
            const srow = statementRows[createDialogRow];
            const info = srow ? parseContraFromStatement(srow.description, srow.refNo) : null;
            const isContra = !!info?.isContra;
            const targetBank = isContra ? bankAccountByLast4.get(String(info?.last4 || '')) : null;
            if (!isContra || targetBank) return null;
            return (
              <TextField
                margin="dense"
                label="Contra Account Name"
                value={createDialogContraName}
                onChange={(e) => setCreateDialogContraName(String(e.target.value || ''))}
                fullWidth
                placeholder={info?.accountNo ? `A/C ${info.accountNo}` : 'Enter contra account'}
              />
            );
          })() : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogRow(null);
              setCreateDialogPartyName('');
              setCreateDialogContraName('');
            }}
            disabled={creatingMatchRow !== null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={createDialogRow === null || creatingMatchRow !== null || !String(createDialogPartyName || '').trim()}
            onClick={async () => {
              if (createDialogRow === null) return;
              await createTransactionFromStatementRow(createDialogRow, createDialogPartyName, createDialogContraName);
            }}
          >
            {creatingMatchRow !== null ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statementSwitchDialogOpen && !!pendingStatementSwitch}
        onClose={() => {
          if (continueImportAfterSwitch) return;
          setStatementSwitchDialogOpen(false);
          setPendingStatementSwitch(null);
          setContinueImportAfterSwitch(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Statement account mismatch</DialogTitle>
        <DialogContent>
          {pendingStatementSwitch ? (
            <Stack spacing={1}>
              <Alert severity="warning">
                This statement appears to be for{' '}
                <strong>
                  {String(pendingStatementSwitch.detected.bank.name || pendingStatementSwitch.detected.bank.bankName || pendingStatementSwitch.detected.bank.id)}
                </strong>{' '}
                (…{pendingStatementSwitch.detected.last4}).
              </Alert>
              <Typography variant="body2" color="text.secondary">
                You currently selected{' '}
                <strong>
                  {String(
                    selectedRow?.account?.name ||
                      selectedRow?.account?.bankName ||
                      selectedAccountId ||
                      '—'
                  )}
                </strong>
                . Switch bank and continue import?
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatementSwitchDialogOpen(false);
              setPendingStatementSwitch(null);
              setContinueImportAfterSwitch(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              if (!pendingStatementSwitch) return;
              setStatementSwitchDialogOpen(false);
              setContinueImportAfterSwitch(false);
              const { ext, text } = pendingStatementSwitch;
              setPendingStatementSwitch(null);
              await importStatementText(ext, text);
            }}
          >
            Import anyway
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!pendingStatementSwitch) return;
              const detectedId = String(pendingStatementSwitch.detected.bank.id || '').trim();
              if (!detectedId) return;
              setSelectedAccountId(detectedId);
              setContinueImportAfterSwitch(true);
            }}
          >
            Switch &amp; import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
